import { useBoxStyle } from "arkh";
import React, { useCallback, useMemo } from "react";
import {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
} from "@gorhom/bottom-sheet";
import { useReportIssueBottomSheetMainScreenFirstSnapPoint } from "./useReportIssueBottomSheetMainScreenFirstSnapPoint";
import { useSizeClass } from "../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { useDeviceOrientation } from "../../common/Utils/useDeviceOrientation";
import { useWindowDimensions } from "react-native";

export function useReportIssueBottomSheetWidth() {
  const { width } = useWindowDimensions();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const orientation = useDeviceOrientation();
  return useMemo(
    () => (isCompact ? width : orientation.landscape ? 400 : 340),
    [isCompact, orientation.landscape, width]
  );
}

export function useReportIssueBottomSheetLayout(props: {
  openExpanded?: boolean;
  containerWidth?: number;
  topSnapPoint?: number | string;
  position?: "left" | "right";
  width: number;
}) {
  const singleSnapPoint = props.openExpanded !== false;

  const firstSnap = useReportIssueBottomSheetMainScreenFirstSnapPoint();
  const secondSnap = props.topSnapPoint ?? "85%";
  const snapPoints = useMemo(
    () => (singleSnapPoint ? [secondSnap] : [firstSnap, secondSnap]),
    [firstSnap, secondSnap, singleSnapPoint]
  );

  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  const bottomSheetWidth = useReportIssueBottomSheetWidth();
  const _width = useMemo(
    () => props.containerWidth ?? props.width,
    [props.containerWidth, props.width]
  );
  const smallMargin = useMemo(() => {
    return !isCompact ? _width * 0.04 : 0;
  }, [isCompact, _width]);
  const bigMargin = useMemo(() => {
    return !isCompact ? _width - bottomSheetWidth - smallMargin : 0;
  }, [bottomSheetWidth, isCompact, smallMargin, _width]);
  const marginLeft = useMemo(() => {
    return props.position === "left" ? smallMargin : bigMargin;
  }, [bigMargin, props.position, smallMargin]);
  const marginRight = useMemo(() => {
    return props.position === "left" ? bigMargin : smallMargin;
  }, [bigMargin, props.position, smallMargin]);

  const bottomSheetStyle = useBoxStyle({
    color: "surface",
    shadow: "strong",
    cornerRadius: "large",
  });

  const style = useMemo(
    () => [
      bottomSheetStyle,
      {
        marginRight,
        marginLeft,
      },
    ],
    [bottomSheetStyle, marginLeft, marginRight]
  );

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) =>
      singleSnapPoint ? (
        <BottomSheetBackdrop
          {...props}
          appearsOnIndex={0}
          disappearsOnIndex={-1}
        />
      ) : (
        <BottomSheetBackdrop {...props} />
      ),
    [singleSnapPoint]
  );

  return {
    style,
    snapPoints,
    firstSnap,
    renderBackdrop: isCompact ? renderBackdrop : undefined,
    detached: !isCompact,
  };
}
